import React, { useMemo, useState } from 'react';

import { useBreakpoint } from '@ast/magma/breakpoints';

import BellRing from '@ast/magma/components/icon/icons/BellRing';

import { Dialog, DialogButtonType } from '@ast/magma/components/dialog';

import { PhoneSmsEnrollmentStatus } from '@app/queryTyping';

import {
  useNotifierContext,
  Alert as AlertType,
} from '@app/common/components/Notifier';

import { ActionListItem } from '@app/widgets/contacts-and-settings/components/ActionListItem';
import { useSettingFieldsContext } from '@app/widgets/contacts-and-settings/contexts/SettingFieldsContext';
import { useUserSettingsTranslation } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';
import { formatPhone } from '@app/widgets/contacts-and-settings/utils/labelFormatters';
import { MappedSubFields } from '@app/widgets/contacts-and-settings/utils/subFieldsMapper';

import { ConfirmPhone } from '../../components/ConfirmPhone/ConfirmPhone';
import { useRemoveSmsEnrollmentMutation } from '../../queries/queryTyping/removeSmsEnrollment';

interface PhoneFieldDetailsState {
  /**
   * boolean value, which indicates if user wants switch ON alerts.
   */
  readonly displayConfirmPhone: boolean;
  readonly displayConfirmUnenroll: boolean;
}

export const SmsEnrollment = ({
  fieldMeta,
}: {
  fieldMeta: MappedSubFields;
}) => {
  const isMobile = useBreakpoint().device === 'mobile';

  const { t } = useUserSettingsTranslation();
  const { addAlerts, clearAlerts } = useNotifierContext();
  const { settingsQuery } = useSettingFieldsContext();

  const smsEnrollmentStatus: PhoneSmsEnrollmentStatus = fieldMeta.SmsEnrollmentStatus?.value.toUpperCase();
  const isSmsEnrollmentOn = PhoneSmsEnrollmentStatus.ENROLLED === smsEnrollmentStatus;
  const isSmsEnrollmentPending = smsEnrollmentStatus === PhoneSmsEnrollmentStatus.OTPPENDING;

  const [state, updateState] = useState<PhoneFieldDetailsState>({
    displayConfirmPhone: isSmsEnrollmentPending,
    displayConfirmUnenroll: false,
  });

  const phoneNumber = useMemo(() => formatPhone(fieldMeta), [fieldMeta]);

  const uniqueId = fieldMeta.UniqueId?.sValue;

  const [removeSmsEnrollment, { error: removeSmsError }] = useRemoveSmsEnrollmentMutation();

  const handleCancelVerification = () => {
    removeSmsEnrollment({
      variables: {
        mobilePhoneId: uniqueId,
      },
    }).then(({ data }) => {
      if (
        data?.removeSmsEnrollment?.smsEnrollmentStatus
        === PhoneSmsEnrollmentStatus.NOTENROLLED
      ) {
        settingsQuery.refetch();
      }
    });

    updateState((currentState) => ({
      ...currentState,
      displayConfirmPhone: false,
    }));
  };
  if (removeSmsError) throw removeSmsError;

  return (
    <ActionListItem
      data-stable-name="EnrollmentActionItem"
      data={t(
        'phone-fields-details.text-messaging-alerts.label|Text messaging alerts label',
        'Text messaging alerts',
      )}
      key={1}
      description={t(
        'phone-fields-details.text-messaging-alerts.description|Text messaging alerts description',
        `Use this number to receive security text messages and account alerts.<br/><br/>
        Text <b>STOP</b> to 86020 to deactivate SMS mobile banking.<br/>
        Text <b>COM</b> to 86020 to fetch list of available commands.<br/>
        Message and data rates may apply. Message frequency depends on account settings.`,
      )}
      getItemIcon={() => <BellRing />}
      checked={isSmsEnrollmentOn || state.displayConfirmPhone}
      clickable={false}
      onChange={({ target }) => {
        const { checked } = target as HTMLInputElement;

        if (!checked && isSmsEnrollmentOn) {
          updateState((currentState) => ({
            ...currentState,
            displayConfirmUnenroll: true,
          }));
        } else if (!checked && isSmsEnrollmentPending) {
          handleCancelVerification();
        } else {
          updateState((currentState) => ({
            ...currentState,
            displayConfirmPhone: checked,
          }));
        }
      }}
    >
      <>
        {state.displayConfirmPhone && (
          <ConfirmPhone
            data={fieldMeta}
            onComplete={() => {
              updateState((currentState) => ({
                ...currentState,
                displayConfirmPhone: false,
              }));
            }}
            onCancel={handleCancelVerification}
            onRequestError={(title: string) => {
              addAlerts([
                {
                  level: 'error',
                  title,
                  withHtmlMarkup: true,
                },
              ]);
            }}
          />
        )}

        {state.displayConfirmUnenroll && (
          <Dialog
            data-stable-name="UnenrollPhoneDialog"
            title={t(
              'confirm-unenroll-phone.title|Unenroll phone dialog title',
              'Unenroll phone number',
            )}
            predefinedWidth="md"
            align={isMobile ? 'bottom' : 'center'}
            defaultButton={DialogButtonType.OK}
            fixedTitle={false}
            onClose={() => {
              updateState((currentState) => ({
                ...currentState,
                displayConfirmUnenroll: false,
              }));
            }}
            showClose={{
              // eslint-disable-next-line i18next/no-literal-string
              'aria-label': t(
                'confirm-unenroll-phone.close-button.aria-label|Accessibility text for close button on confirm unenroll phone dialog',
                'Close delete modal',
              ),
            }}
            buttons={[
              {
                type: DialogButtonType.Cancel,
                label: t(
                  'confirm-unenroll-phone.cancel-button|Confirm unenroll phone dialog cancel button',
                  'Cancel',
                ),
              },
              {
                type: DialogButtonType.OK,
                label: t(
                  'confirm-unenroll-phone.delete-button|Confirm unenroll phone dialog delete button label',
                  'Unenroll',
                ),
                onClick: async () => {
                  removeSmsEnrollment({
                    variables: {
                      mobilePhoneId: uniqueId,
                    },
                  }).then(({ data }) => {
                    if (
                      data?.removeSmsEnrollment?.smsEnrollmentStatus
                      === PhoneSmsEnrollmentStatus.NOTENROLLED
                    ) {
                      clearAlerts();
                      addAlerts([
                        {
                          level: 'success',
                          title: t(
                            'confirm-unenroll-phone.successful-message|Successful message for unenroll phone',
                            'Your phone number has been successfully unenrolled from text messaging.',
                          ),
                        },
                      ] as AlertType[]);

                      settingsQuery.refetch();
                    }

                    updateState((currentState) => ({
                      ...currentState,
                      displayConfirmUnenroll: false,
                    }));
                  });
                  return true;
                },
              },
            ]}
          >
            <div>
              {
                t(
                  'confirm-unenroll-phone.text|Text content for unenroll phone dialog',
                  'Are you sure you want to unenroll phone number <strong>{{phoneNumber}}</strong> from receiving security text messages and account alerts?',
                  { phoneNumber },
                )
              }
            </div>
          </Dialog>
        )}
      </>
    </ActionListItem>
  );
};
